// src/browser/IndexedDbPersistence.ts
var CHANGE_LOG = "__changes";
function request(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
function done(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}
function openDatabase(factory, name, version, stores) {
  return new Promise((resolve, reject) => {
    const req = version === void 0 ? factory.open(name) : factory.open(name, version);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const store of stores) {
        if (!db.objectStoreNames.contains(store)) {
          db.createObjectStore(store, store === CHANGE_LOG ? { autoIncrement: true } : { keyPath: "id" });
        }
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
var IndexedDbPersistence = class _IndexedDbPersistence {
  constructor(db, entityTypes, now) {
    this.db = db;
    this.entityTypes = entityTypes;
    this.now = now;
  }
  db;
  entityTypes;
  now;
  /** Open (creating or upgrading) the database so it holds every listed entity type. */
  static async open(options) {
    const factory = options.factory ?? globalThis.indexedDB;
    if (!factory) throw new Error("IndexedDbPersistence: this environment has no IndexedDB");
    const stores = [...options.entityTypes, CHANGE_LOG];
    let db = await openDatabase(factory, options.databaseName, void 0, stores);
    if (stores.some((s) => !db.objectStoreNames.contains(s))) {
      const next = db.version + 1;
      db.close();
      db = await openDatabase(factory, options.databaseName, next, stores);
    }
    return new _IndexedDbPersistence(db, new Set(options.entityTypes), options.now ?? (() => (/* @__PURE__ */ new Date()).toISOString()));
  }
  close() {
    this.db.close();
  }
  store(entityType) {
    if (!this.entityTypes.has(entityType)) {
      throw new Error(`IndexedDbPersistence: not opened with entity type "${entityType}"`);
    }
    return entityType;
  }
  async write(entityType, op, id, row) {
    const tx = this.db.transaction([this.store(entityType), CHANGE_LOG], "readwrite");
    if (row) tx.objectStore(entityType).put(row);
    else tx.objectStore(entityType).delete(id);
    const change = { op, entityType, id, row, at: this.now() };
    tx.objectStore(CHANGE_LOG).add(change);
    await done(tx);
  }
  async create(entityType, data) {
    const id = typeof data.id === "string" && data.id.length > 0 ? data.id : crypto.randomUUID();
    await this.write(entityType, "create", id, { ...data, id, updatedAt: this.now() });
    return { id };
  }
  async update(entityType, id, data) {
    const existing = await this.getById(entityType, id);
    if (!existing) return;
    await this.write(entityType, "update", id, { ...existing, ...data, id, updatedAt: this.now() });
  }
  async delete(entityType, id) {
    await this.write(entityType, "delete", id, null);
  }
  async getById(entityType, id) {
    const tx = this.db.transaction(this.store(entityType), "readonly");
    const row = await request(tx.objectStore(entityType).get(id));
    return row ?? null;
  }
  async list(entityType) {
    const tx = this.db.transaction(this.store(entityType), "readonly");
    return request(tx.objectStore(entityType).getAll());
  }
  async countRows(entityType) {
    const tx = this.db.transaction(this.store(entityType), "readonly");
    return request(tx.objectStore(entityType).count());
  }
  /** The write log, oldest first — the seam a later `sync` replays. */
  async changes() {
    const tx = this.db.transaction(CHANGE_LOG, "readonly");
    return request(tx.objectStore(CHANGE_LOG).getAll());
  }
};

export { CHANGE_LOG, IndexedDbPersistence };
//# sourceMappingURL=browser.js.map
//# sourceMappingURL=browser.js.map